import { useQueryClient } from "@tanstack/react-query";
import { useId, type SubmitEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useCreateAccount, useLogin } from "@/api/bale";
import { Button } from "@/components/Button";
import { Form } from "@/components/Form";
import { Input } from "@/components/Input";

export function SignupForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const login = useLogin({
    mutation: {
      onSuccess: async () => {
        void navigate("/overview", { state: { showProfileSetup: true } });
        await queryClient.resetQueries();
      },
    },
  });
  const createAccount = useCreateAccount({
    mutation: {
      onSuccess: (_data, variables) => {
        login.mutate({
          data: {
            id: variables.data.id,
            password: variables.data.password,
            remember_me: true,
          },
        });
      },
    },
  });

  function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    const confirmPasswordField = event.currentTarget.elements.namedItem("confirm-password") as HTMLInputElement;

    const form = new FormData(event.currentTarget);
    const id = form.get("username")! as string;
    const password = form.get("password")! as string;
    const email = form.get("email")! as string;
    const confirmPassword = form.get("confirm-password")! as string;
    if (password !== confirmPassword) {
      confirmPasswordField.setCustomValidity(t("passwords-do-not-match"));
    }
    if (!event.currentTarget.reportValidity()) return;

    createAccount.mutate({
      data: { login_method: "Password", id, email, password },
    });
  }

  const loading = createAccount.isPending || login.isPending;
  const labelId = useId();

  return (
    <div className="flex flex-col items-center">
      <h2 className="mb-6 text-center font-title whitespace-nowrap" id={labelId}>
        {t("signup-title")}
      </h2>
      <Form
        error={createAccount.error}
        onSubmit={handleSubmit}
        className="flex w-full flex-col gap-6"
        aria-labelledby={labelId}
      >
        <Input name="username" type="text" required placeholder={t("username")} disabled={loading} />
        <Input name="email" type="email" required placeholder={t("email")} disabled={loading} />
        <Input name="password" type="password" required placeholder={t("password")} disabled={loading} />
        <Input
          name="confirm-password"
          type="password"
          required
          placeholder={t("confirm-password")}
          disabled={loading}
        />
        <div className="flex flex-col gap-2">
          <Button type="submit" disabled={createAccount.isPending}>
            {t("signup-action")}
          </Button>
          <p className="text-center font-hint">{t("signup-disclaimer")}</p>
        </div>
      </Form>
    </div>
  );
}
